<template>
  <v-edit-dialog
    v-model="innerValue"
    :large="true"
    cancel-text="閉じる"
    save-text="適用"
    @save="onSave"
    @close="onClose">

    <div>
      {{innerValue}}
      <v-icon
        color="blue"
        small>
        mdi-pencil
      </v-icon>
    </div>

    <template v-slot:input>

      <dd-date-box
        v-if="editType=='date'"
        v-model="innerValue"
        ref="dd-edit-dialog-dd-date-box"
        single-line
        :required="required"
        @keydown.native.enter="enterBlur"
        counter/>

      <dd-numeric-box
        v-if="editType=='num'"
        v-model="innerValue"
        ref="dd-edit-dialog-dd-numeric-box"
        single-line
        :integerDigit="numIntegerDigit"
        :minorDigit="numMinorDigit"
        :max="numMax"
        :min="numMin"
        :required="required"
        @keydown.native.enter="enterBlur"
        counter/>

      <dd-time-box
        v-if="editType=='time'"
        v-model="innerValue"
        ref="dd-edit-dialog-dd-time-box"
        single-line
        :required="required"
        @keydown.native.enter="enterBlur"
        counter/>

      <dd-year-month-box
        v-model="innerValue"
        v-if="editType=='ym'"
        ref="dd-edit-dialog-dd-year-month-box"
        single-line
        :required="required"
        @keydown.native.enter="enterBlur"
        counter/>

      <dd-text-box
        v-if="editType=='text'"
        v-model="innerValue"
        ref="dd-edit-dialog-dd-text-box"
        single-line
        :required="required"
        @keydown.native.enter="enterBlur"
        counter/>

    </template>

  </v-edit-dialog>
</template>
<script>
import controls from '@/rahmen/controls'
import DdTextBox from '@/components/DdTextBox'
import DdDateBox from '@/components/DdDateBox'
import DdNumericBox from '@/components/DdNumericBox'
import DdYearMonthBox from '@/components/DdYearMonthBox'
import DdTimeBox from '@/components/DdTimeBox'
import filter from '@/rahmen/utils/filters'

/**
 * @file DDv2用編集ダイアログ
 * @namespace components
 */

/**
 * @vue-prop {String} [value=''] - 編集対象値
 * @vue-prop {Number} [valueType=99] - 編集値型
 * @vue-event {Object} save - 値保存時イベント(返却値：入力値)
 * @vue-event {Boolean} close - ダイアログクローズイベント(返却値：Textboxのvalid)
 * @see https://vuetifyjs.com/ja/api/v-edit-dialog/
 * @example
  <template
    v-slot:[`item.chakuchiYmd`]="p">

    <dd-edit-dialog
      v-model="p.item.chakuchiYmd"
      :valueType="Ctrls.InputTypes.Date"
      @save="p.item.chakuchiYmd = $event"
      @close="onMeisaiEditClosed($event, p.item)"/>

  </template>
 */
export default {
  name: 'DdEditDialog',
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    /**
     * 入力タイプ
     * @see controls.InputTypes
     */
    valueType: {
      type: Number,
      default: 99
    },
    /**
     * 数値正数部桁数指定
     */
    numIntegerDigit: {
      type: Number,
      default: String(Number.MAX_SAFE_INTEGER).length
    },
    /**
     * 数値小数部桁数指定
     */
    numMinorDigit: {
      type: Number,
      default: 0
    },
    /**
     * 数値入力可能最大値
     */
    numMax: {
      type: Number,
      default: Number.MAX_SAFE_INTEGER
    },
    /**
     * 数値入力可能最小値
     */
    numMin: {
      type: Number,
      default: Number.MAX_SAFE_INTEGER * -1
    },
    /**
     * 必須指定
     */
    required: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    innerValue: '',
    defaultValue: '',
    isValid: false,
    defineTypes: [
      { key: controls.InputTypes.Number, type: 'text', refs: 'dd-edit-dialog-dd-text-box' },
      { key: controls.InputTypes.Alphanumeric, type: 'text', refs: 'dd-edit-dialog-dd-text-box' },
      { key: controls.InputTypes.FullAngle, type: 'text', refs: 'dd-edit-dialog-dd-text-box' },
      { key: controls.InputTypes.FullWidthKana, type: 'text', refs: 'dd-edit-dialog-dd-text-box' },
      { key: controls.InputTypes.HalfWidthKana, type: 'text', refs: 'dd-edit-dialog-dd-text-box' },
      { key: controls.InputTypes.Hiragana, type: 'text', refs: 'dd-edit-dialog-dd-text-box' },
      { key: controls.InputTypes.AlphanumericWithSymbol, type: 'text', refs: 'dd-edit-dialog-dd-text-box' },
      { key: controls.InputTypes.HalfAngle, type: 'text', refs: 'dd-edit-dialog-dd-text-box' },
      { key: controls.InputTypes.Free, type: 'text', refs: 'dd-edit-dialog-dd-text-box' },
      { key: controls.InputTypes.Time, type: 'time', refs: 'dd-edit-dialog-dd-time-box' },
      { key: controls.InputTypes.YearMonth, type: 'ym', refs: 'dd-edit-dialog-dd-year-month-box' },
      { key: controls.InputTypes.Numerical, type: 'num', refs: 'dd-edit-dialog-dd-numeric-box' },
      { key: controls.InputTypes.Date, type: 'date', refs: 'dd-edit-dialog-dd-date-box' }
    ]
  }),
  components: {
    DdTextBox,
    DdDateBox,
    DdNumericBox,
    DdYearMonthBox,
    DdTimeBox
  },
  watch: {
    value (n, o) {
      if (n !== o) {
        this.innerValue = n
      }
    }
  },
  mounted () {
    this.innerValue = this.value
    this.defaultValue = this.value
  },
  computed: {
    editType () {
      let result = ''
      const t = this.defineTypes.find((t) => {
        return t.key === this.valueType
      })
      if (t) {
        result = t.type
      }
      return result
    },
    editTypeRefs () {
      let result = ''
      const t = this.defineTypes.find((t) => {
        return t.key === this.valueType
      })
      if (t) {
        result = t.refs
      }
      return result
    }
  },
  methods: {
    enterBlur (e) {
      const elem = this.$refs[this.editTypeRefs]
      elem.onBlur()
    },
    onSave () {
      const elem = this.$refs[this.editTypeRefs]
      elem[controls.VALIDATE_METHOD]()
      if (elem.invalidMessage !== undefined) {
        this.isValid = false
        return
      }
      this.isValid = true
      if (this.editType === 'num') {
        this.innerValue = filter.numericFormatter(this.innerValue)
        this.defaultValue = this.innerValue
        this.$emit('save', this.innerValue.replaceAll(',', ''))
        return
      }
      this.defaultValue = this.innerValue
      this.$emit('save', this.innerValue)
    },
    onClose () {
      this.$nextTick().then(() => {
        this.innerValue = this.defaultValue
        this.$emit('close', this.isValid)
      })
    }
  }
}
</script>
